import { useNavigate } from 'react-router-dom';
import Badge from '@/components/base/Badge';
import { STATUS_CONFIG } from '@/lib/projectStatus';
import { formatDate, formatCurrency, getProjectAlerts } from '@/lib/alerts';
import type { Project } from '@/types';

interface ProjectCardProps {
  project: Project;
  index?: number;
  receiptDates?: string[];
}

export default function ProjectCard({ project, index = 0, receiptDates }: ProjectCardProps) {
  const navigate = useNavigate();
  const alerts = getProjectAlerts(project, new Date(), receiptDates);
  const status = STATUS_CONFIG[project.status];

  const borderClass =
    alerts.contractState === 'finalizado' || alerts.contractState === 'por_finalizar' || alerts.billingState === 'hoy'
      ? 'border-red-300 hover:border-red-400'
      : alerts.billingState === 'pendiente'
        ? 'border-amber-300 hover:border-amber-400'
        : 'border-background-200 hover:border-primary-300';

  const open = () => navigate(`/proyectos/${project.id}`);

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          open();
        }
      }}
      style={{ animationDelay: `${Math.min(index, 8) * 40}ms` }}
      className={[
        'group flex cursor-pointer flex-col rounded-lg border bg-background-50 p-4 transition-all duration-200 animate-fade-up md:p-5',
        'hover:-translate-y-0.5',
        borderClass,
      ].join(' ')}
    >
      <div className="flex items-start gap-3">
        <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md border border-background-200 bg-background-100">
          {project.logo_url ? (
            <img
              src={project.logo_url}
              alt={`Logo de ${project.name}`}
              title={`${project.name} — IZZY Corporation`}
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="flex h-full w-full items-center justify-center text-foreground-400">
              <i className="ri-folder-3-line text-xl"></i>
            </span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="truncate font-heading text-sm font-bold text-foreground-950 md:text-base">
            {project.name}
          </h3>
          <p className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-foreground-500">
            <i className="ri-building-2-line text-sm"></i>
            {project.clients?.name || 'Cliente no asignado'}
          </p>
        </div>

        <Badge tone={status.tone} icon={status.icon}>
          {status.label}
        </Badge>
      </div>

      <p className="mt-3 line-clamp-2 min-h-[2.5rem] text-xs leading-relaxed text-foreground-600 md:text-sm">
        {project.description || 'Sin descripción registrada.'}
      </p>

      <div className="mt-3 grid grid-cols-2 gap-3 border-t border-background-200 pt-3">
        <div>
          <p className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-foreground-400">
            <i className="ri-calendar-check-line text-sm"></i>
            Próx. cobro
          </p>
          <p className="mt-0.5 text-sm font-semibold text-foreground-900">
            {formatDate(alerts.nextBillingDate)}
          </p>
          <p className="text-[11px] text-foreground-500">
            {project.monthly_amount !== null && project.monthly_amount !== undefined
              ? `Mensualidad: ${formatCurrency(project.monthly_amount)}`
              : 'Monto no definido'}
          </p>
        </div>
        <div>
          <p className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-foreground-400">
            <i className="ri-file-shield-2-line text-sm"></i>
            Contrato hasta
          </p>
          <p className="mt-0.5 text-sm font-semibold text-foreground-900">
            {formatDate(project.contract_end)}
          </p>
          <p className="text-[11px] text-foreground-500">
            {project.contract_start ? `Desde ${formatDate(project.contract_start)}` : 'Sin fecha de inicio'}
          </p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {alerts.billingState === 'pendiente' && (
          <Badge tone="warning" icon="ri-alarm-warning-line">
            <span className="font-bold">COBRO PENDIENTE</span>
          </Badge>
        )}

        {alerts.billingState === 'hoy' && (
          <Badge tone="danger" icon="ri-notification-3-line">
            <span className="font-bold text-red-700">SE COBRA HOY</span>
          </Badge>
        )}

        {alerts.billingState === 'pagado' && (
          <Badge tone="success" icon="ri-checkbox-circle-line">
            <span className="font-bold">PAGADO</span>
          </Badge>
        )}

        {alerts.contractState === 'por_finalizar' && (
          <Badge tone="danger" icon="ri-error-warning-line">
            <span className="font-bold text-red-700">POR FINALIZAR</span>
          </Badge>
        )}

        {alerts.contractState === 'finalizado' && (
          <Badge tone="danger" icon="ri-close-circle-line">
            <span className="font-bold text-red-700">CONTRATO FINALIZADO</span>
          </Badge>
        )}

        {!alerts.hasAlert && alerts.billingState !== 'pagado' && (
          <span className="flex items-center gap-1.5 text-xs text-foreground-500">
            <i className="ri-checkbox-circle-line text-sm text-primary-500"></i>
            Sin alertas pendientes
          </span>
        )}

        <span className="ml-auto flex items-center gap-1 text-xs font-medium text-primary-700 opacity-0 transition-opacity group-hover:opacity-100">
          Ver detalle
          <i className="ri-arrow-right-line text-sm"></i>
        </span>
      </div>
    </article>
  );
}